import React, { useState, useEffect } from 'react';
import {
    Typography,
    Skeleton,
} from '@mui/material';
import TabsLayout from '../Common/TabsLayout';
import AssessmentForm from './AssesmentForm';
import AssessmentTable from './AssessmentTable';
import FeedbackTable from './FeedbackTable';
import ProgramTable from './ProgramTable';


const AssessmentTabs = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [userData, setUserData] = useState(null);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [isBasicUser, setIsBasicUser] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const handleEditAssessment = (newValue, assessment) => {
        setSelectedAssessment(assessment);
        setActiveStep(newValue);
    };

    const handleChange = (event, newValue) => {
        setSelectedAssessment(null);
        setActiveStep(newValue);
    };

    useEffect(() => {
        const userDataFromStorage = localStorage.getItem("userData");
        if (userDataFromStorage) {
            const parsed = JSON.parse(userDataFromStorage);
            setUserData(parsed);

            const role = parsed.user_role?.toLowerCase();
            const basicUser = role === 'user' || role === 'student' || role === 'trainee';
            setIsBasicUser(basicUser);
        }
        
        // Simulate loading time for better UX
        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    }, []);

    useEffect(() => {
        if (userData) {
            const baseTabs = [
                {
                    id: "programs",
                    label: "Programs",
                    title: "Program Management",
                    description: "Create and manage program codes for assessments"
                },
                {
                    id: "assessments",
                    label: "Assessments",
                    title: "Assessment Management",
                    description: "View, manage, and track all assessments and submissions"
                },
                {
                    id: "feedback",
                    label: "Feedback",
                    title: "Assessment Feedback",
                    description: "Provide and view feedback for assessment submissions"
                },
            ];

            if (!isBasicUser) {
                baseTabs.push(
                    {
                        id: "assessment-form",
                        label: "Assessment Form",
                        title: "Create Assessment",
                        description: "Build and configure new assessments with questions and settings"
                    },
                );
            }

            setTabs(baseTabs);
        }
    }, [userData, isBasicUser, selectedAssessment]);

    if (isLoading || !userData || tabs.length === 0) {
        return (
            <div style={{ background: 'white', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={64}
                    sx={{ borderRadius: 1 }}
                />
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={400}
                    sx={{ borderRadius: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins' }}>
                    Loading your assessments...
                </Typography>
            </div>
        );
    }

    const renderTabContent = (activeTab) => {
        if (activeTab === 'programs') {
            return <ProgramTable />;
        }
        if (activeTab === 'assessments') {
            return <AssessmentTable onEdit={handleEditAssessment} />;
        }
        if (activeTab === 'feedback') {
            return <FeedbackTable />;
        }
        if (activeTab === 'assessment-form') {
            return (
                <AssessmentForm
                    key={selectedAssessment ? selectedAssessment.id : 'new'}
                    assessment={selectedAssessment}
                />
            );
        }
        return null;
    };

    return (
        <TabsLayout
            tabs={tabs}
            title="Assessment Management"
            defaultTab={tabs[0]?.id}
            onTabChange={(tabId) => {
                const tabIndex = tabs.findIndex(tab => tab.id === tabId);
                setActiveStep(tabIndex);
            }}
        >
            {renderTabContent}
        </TabsLayout>
    );
};

export default AssessmentTabs;